import { BASE_URL } from './constants';
import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';

export interface CampaignLockStatus {
  campaign_id: string;
  is_locked: boolean;
  locked_by_user_name: string | null;
  locked_at: string | null;
  lock_expires_at: string | null;
}

export interface GetLockStatusResponse {
  lock: CampaignLockStatus;
}

export interface AcquireLockResponse {
  lock: CampaignLockStatus & {
    locked_by_user_name: string;
    locked_at: string;
    lock_expires_at: string | null;
  };
}

export interface ReleaseLockResponse {
  message: string;
}

export class CampaignLockedError extends Error {
  constructor(message: string = 'Campaign is locked by another user') {
    super(message);
    this.name = 'CampaignLockedError';
  }
}

function getLockUrl(campaignId: string): string {
  return `${BASE_URL}/v1/campaigns/${campaignId}/lock`;
}

export async function getLockStatus(
  campaignId: string
): Promise<GetLockStatusResponse> {
  const url = getLockUrl(campaignId);
  const userSessionToken = getUserSessionCookie();

  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      data?.detail ?? `Failed to get lock status: ${response.status}`
    );
  }

  return response.json();
}

export async function acquireLock(
  campaignId: string
): Promise<AcquireLockResponse> {
  const url = getLockUrl(campaignId);
  const userSessionToken = getUserSessionCookie();

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });

  if (response.status === 409) {
    const data = await response.json().catch(() => ({}));
    throw new CampaignLockedError(
      data?.detail ?? 'Campaign is locked by another user'
    );
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      data?.detail ?? `Failed to acquire lock: ${response.status}`
    );
  }

  return response.json();
}

export async function releaseLock(
  campaignId: string
): Promise<ReleaseLockResponse> {
  const url = getLockUrl(campaignId);
  const userSessionToken = getUserSessionCookie();

  const response = await apiFetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      data?.detail ?? `Failed to release lock: ${response.status}`
    );
  }

  return response.json();
}
